<template>
  <div class="step-audience">
    <template>
      <label class="d-block">Who receives this campaign</label>
      <segmented-toggle
        :value="audienceMode"
        variant="pills"
        :options="[
          { value: 'new', label: 'Build an audience' },
          { value: 'saved', label: 'Use a saved audience' }
        ]"
        @input="$emit('update:audience-mode', $event)"
      />

    <div v-if="audienceMode === 'saved'" class="mt-4">
      <div class="row">
        <div class="col-md-6">
          <label>Saved audience</label>
          <base-input>
            <el-select
              :value="savedAudienceId"
              filterable
              placeholder="Choose a saved audience"
              class="select-primary w-100"
              @input="$emit('update:saved-audience-id', $event)"
            >
              <el-option
                v-for="audience in audiences"
                :key="audience.id"
                :value="audience.id"
                :label="audience.name"
                class="select-primary"
              />
            </el-select>
          </base-input>
        </div>
      </div>
      <div v-if="selectedAudience" class="step-audience__manage">
        <base-button variant="outlined" tone="primary" size="sm" @click="$emit('edit-audience', selectedAudience)">
          <i class="tim-icons icon-pencil"></i><span>Edit<span class="d-none d-sm-inline">&nbsp;audience</span></span>
        </base-button>
        <base-button variant="outlined" tone="primary" size="sm" @click="$emit('delete-audience', selectedAudience)">
          <i class="tim-icons icon-trash-simple"></i><span>Delete<span class="d-none d-sm-inline">&nbsp;audience</span></span>
        </base-button>
      </div>
      <info-hint v-if="!audiences.length" style="width: 100%">
        No saved audiences yet. Build one on this step and turn on <strong>save this audience</strong> below.
      </info-hint>
    </div>
    </template>

    <div v-if="audienceMode !== 'saved'" class="mt-4">
      <customer-filter-panel
        :value="audienceFilter"
        :mode="campaignType === 'ongoing' ? 'ongoing' : 'one_time'"
        :games="games"
        :scoped-group-ids="scopedGroupIds"
        @input="$emit('update:audience-filter', $event)"
      />
    </div>

    <!-- Recipient count readout: no global stat primitive exists; this local
         block is layout + one number only, colors from the theme palette -->
    <div class="step-audience__count" :class="{ 'step-audience__count--error': previewError }">
      <template v-if="previewLoading && !domainChecking">
        <i class="el-icon-loading step-audience__count-loader"></i>
        <label class="m-0">Counting recipients</label>
      </template>
      <template v-else-if="previewError">
        <i class="tim-icons icon-alert-circle-exc text-danger"></i>
        <label class="m-0">{{ previewError }}</label>
      </template>
      <template v-else-if="previewCount !== null">
        <span class="step-audience__count-number text-white">{{ previewCount.toLocaleString() }}</span>
        <label class="m-0">{{ campaignType === 'ongoing' ? ongoingCountLabel : hasFilters ? 'approximate recipients, recalculated at send time' : 'subscribed customers, everyone is included, recalculated at send time' }}{{ estimatedCost ? `, estimated cost ${estimatedCost}` : '' }}</label>
        <base-button v-if="previewCount > 0" variant="outlined" tone="primary" size="sm" class="step-audience__count-action" @click="$emit('view-recipients')">
          <i class="tim-icons icon-bullet-list-67"></i>{{ campaignType === 'ongoing' ? 'View customers' : 'View recipients' }}
        </base-button>
        <template v-if="hygiene && previewCount > 0">
          <label v-if="hygiene.suppressed_total > 0" class="m-0 step-audience__hygiene text-warning">
            <i class="tim-icons icon-alert-circle-exc"></i>
            {{ hygiene.suppressed_total.toLocaleString() }} will be skipped as undeliverable: {{ hygieneBreakdown }}
          </label>
          <label v-else class="m-0 step-audience__hygiene">
            <i class="tim-icons icon-check-2 text-success"></i>
            No known undeliverable addresses
          </label>
          <label v-if="hygiene.suppressed_total > 0" class="m-0 step-audience__hygiene">
            <strong class="text-white">{{ Math.max(previewCount - hygiene.suppressed_total, 0).toLocaleString() }}</strong> will be sent
          </label>
          <label v-if="domainChecking" class="m-0 step-audience__hygiene">
            <i class="el-icon-loading"></i>
            Checking {{ (hygiene.unchecked_domains || 0).toLocaleString() }} {{ hygiene.unchecked_domains === 1 ? 'domain' : 'domains' }} for deliverability
          </label>
          <label v-else-if="hygiene.unchecked_domains > 0" class="m-0 step-audience__hygiene">
            {{ hygiene.unchecked_domains.toLocaleString() }} {{ hygiene.unchecked_domains === 1 ? 'domain has' : 'domains have' }} not been checked for deliverability yet
            <base-button variant="outlined" tone="primary" size="sm" class="ml-2" @click="$emit('check-domains')">
              <i class="tim-icons icon-refresh-02"></i>Check now
            </base-button>
          </label>
        </template>
      </template>
      <template v-else>
        <label class="m-0">The recipient count will appear here once an audience is chosen.</label>
      </template>
    </div>

    <p v-if="sampleNames.length" class="mt-2 mb-0 step-audience__muted">
      <small><strong>Includes</strong> {{ sampleNames.join(', ') }}</small>
    </p>

    <div v-if="audienceMode === 'new'" class="step-audience__save">
      <toggle-field
        :value="saveAudience"
        :active-value="true"
        :inactive-value="false"
        label="Save this audience for reuse"
        hint="Saved audiences appear in the picker above for future campaigns."
        @input="$emit('update:save-audience', $event)"
      />
      <div v-if="saveAudience" class="row mt-3">
        <div class="col-md-6">
          <base-input label="Audience name" class="mb-0">
            <el-input
              :value="audienceName"
              placeholder="e.g. Birthday customers"
              maxlength="255"
              @input="$emit('update:audience-name', $event)"
            />
          </base-input>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import ToggleField from '@/components/PaymentPages/ToggleField.vue'
import CustomerFilterPanel from '@/components/CustomerFilterPanel.vue'

const props = defineProps({
  // 'one_time' sends once; 'ongoing' checks a daily condition (set on the
  // Schedule step, merged onto the filter by the editor) and sends every day.
  campaignType: { type: String, default: 'one_time' },
  endsOn: { type: String, default: '' }, // yyyy-MM-dd, ongoing only; shapes the count label
  conditionDays: { type: Number, default: 7 }, // ongoing only; shapes the count label
  audienceMode: { type: String, default: 'new' },
  audienceFilter: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  savedAudienceId: { type: Number as PropType<number | null>, default: null },
  audiences: { type: Array as PropType<any[]>, default: () => [] },
  games: { type: Array as PropType<any[]>, default: () => [] },
  previewCount: { type: Number as PropType<number | null>, default: null },
  previewSample: { type: Array as PropType<any[]>, default: () => [] },
  previewLoading: { type: Boolean, default: false },
  previewError: { type: String, default: '' },
  hygiene: { type: Object as PropType<{ suppressed_total: number; reasons: Record<string, number>; unchecked_domains?: number } | null>, default: null },
  domainChecking: { type: Boolean, default: false },
  estimatedCost: { type: String, default: '' },
  saveAudience: { type: Boolean, default: false },
  audienceName: { type: String, default: '' },
  scopedGroupIds: { type: Array as PropType<number[] | null>, default: null }
})

defineEmits([
  'update:audience-mode',
  'update:audience-filter',
  'update:saved-audience-id',
  'update:save-audience',
  'update:audience-name',
  'edit-audience',
  'delete-audience',
  'view-recipients',
  'check-domains'
])

// Ongoing estimate wording: the count is the reach, not today's slice
const ongoingCountLabel = computed(() => {
  const days = Number.isInteger(props.conditionDays) ? props.conditionDays : 0
  const when = days === 0 ? 'on their birthday' : days === 1 ? '1 day before their birthday' : `${days} days before their birthday`
  if (props.endsOn) return `customers will be emailed ${when} before the stop date, one email each`
  return `customers with a birthday on file will be emailed ${when} over the coming year, one email each`
})

const selectedAudience = computed(() => props.audiences.find((a: any) => a.id === props.savedAudienceId) || null)

const hasFilters = computed(() => Object.keys(props.audienceFilter || {}).length > 0 || props.audienceMode === 'saved')

// Undeliverable breakdown, only the reasons with a count. Wording matches the
// reason labels on the campaign report.
const HYGIENE_LABELS: Record<string, string> = {
  suppressed_prior_bounce: 'previously bounced',
  suppressed_complaint: 'previously marked as spam',
  suppressed_invalid_address: 'invalid addresses',
  suppressed_disposable_domain: 'disposable addresses',
  suppressed_no_mx: 'on domains that cannot receive mail'
}
const hygieneBreakdown = computed(() => {
  const reasons = props.hygiene?.reasons || {}
  const parts: string[] = []
  for (const [code, label] of Object.entries(HYGIENE_LABELS)) {
    const n = reasons[code] || 0
    if (n > 0) parts.push(`${n.toLocaleString()} ${label}`)
  }
  return parts.join(', ')
})

// Dedupe the sample for display and never render a blank name: fall back to
// the email address when a customer record has no name on file.
const sampleNames = computed(() => {
  const seen = new Set<string>()
  const names: string[] = []
  for (const person of props.previewSample as any[]) {
    const display = `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.email || ''
    if (!display || seen.has(display)) continue
    seen.add(display)
    names.push(display)
    if (names.length >= 5) break
  }
  return names
})
</script>

<script lang="ts">
export default { name: 'campaign-step-audience' }
</script>

<style lang="scss" scoped>
// Layout only; all controls, labels, hints, and toggles are global primitives.
.step-audience {
  // layout only: row CTAs under the picker
  &__manage {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    // Phones: both pills on one line; the theme stretches .btn full-width below sm
    @media (max-width: 575px) {
      flex-wrap: nowrap;
      .btn {
        width: auto;
        flex: 0 0 auto;
      }
    }
  }

  &__count {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 1.5rem;
    padding: 0.75rem 1.25rem;
    max-width: 100%;
    border-radius: 0.4285rem;
    border: 1px solid rgba(225, 78, 202, 0.35);
    background: rgba(225, 78, 202, 0.07);

    // Phones: full-width box, labels wrap, action on its own line
    @media (max-width: 575px) {
      display: flex;
      width: 100%;
      .step-audience__count-action {
        flex: 0 0 100%;
        width: auto;
        align-self: flex-start;
      }
    }

    &--error {
      border-color: rgba(253, 93, 147, 0.5);
      background: rgba(253, 93, 147, 0.08);
    }
  }

  // layout only: the undeliverable lines sit on their own rows under the count
  &__hygiene {
    flex: 0 0 100%;
  }

  &__count-number {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  &__count-loader {
    color: #e14eca;
    font-size: 1.25rem;
    align-self: center;
  }

  &__save {
    margin-top: 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(125, 125, 125, 0.15);
  }


  &__muted {
    color: rgba(255, 255, 255, 0.75);
  }
}

.white-content .step-audience__muted {
  color: rgba(0, 0, 0, 0.7);
}
</style>
